import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, User as UserIcon } from 'lucide-react';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { authService } from '@/features/auth/services/authService';
import { notificationService } from '@/services/notificationService';
import { playClickSound } from '@/utils/soundUtils';
import { FORGOT_PASSWORD_LOGIN_PATH } from '@/features/auth/constants/authRoutes';
import { getPendingCheckout } from '@/features/finance/utils/pendingCheckout';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  Field,
  Input,
  SegmentedControl,
  cx,
  focusRing,
  type SegmentItem,
} from '@/components/ui';
import { AuthMethodLabel, AuthPhoneInput, AuthSectionLabel, PasswordInput } from './AuthFormParts';

type AuthTab = 'username' | 'phone' | 'email';

const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

/** Inline text link inside a sentence ("…login via phone number or email."). */
const inlineLinkClass = cx('rounded-control text-link underline underline-offset-2 hover:text-primary', focusRing);

/**
 * Sign-in popup on Dialog. Sign in: Username | Phone | Email.
 * Register: Phone | Email (OTP). Username tab supports 2FA (email OTP) when enabled.
 * Same controls as the login page (AuthFormParts); its own flow (register mode, forgot → /login?forgot=1).
 */
export default function AuthModalHost() {
  const { isOpen, mode, closeAuthModal, setAuthMode, notifyAuthenticated } = useAuthModal();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tab, setTab] = useState<AuthTab>('username');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [sendingCode, setSendingCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Username+password tab: credentials OK and waiting for email OTP (2FA). */
  const [username2FA, setUsername2FA] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [twoFactorMaskedEmail, setTwoFactorMaskedEmail] = useState('');

  const isRegister = mode === 'register';
  const orderSaved = !!getPendingCheckout();
  const otpTab = isRegister || tab !== 'username' || username2FA;

  // Dialog owns Escape, focus and the scroll lock; closing resets the password / 2FA state.
  useEffect(() => {
    if (isOpen) return;
    setShowPassword(false);
    setUsername2FA(false);
    setTwoFactorEmail('');
    setTwoFactorMaskedEmail('');
  }, [isOpen]);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = window.setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [otpCooldown]);

  useEffect(() => {
    if (isRegister && tab === 'username') setTab('phone');
  }, [isRegister, tab]);

  const resetErrors = () => setError(null);

  const resetUsername2FA = () => {
    setUsername2FA(false);
    setTwoFactorEmail('');
    setTwoFactorMaskedEmail('');
  };

  const reportError = (msg: string) => {
    setError(msg);
    notificationService.error(msg);
  };

  const completeLogin = async (opts?: { isRegister?: boolean }) => {
    if (opts?.isRegister) {
      notificationService.success(t('auth.registerSuccess', 'Account created — you are signed in'));
    } else {
      notificationService.success(t('auth.loginSuccessRedirecting', 'Login successful!'), { duration: 2000 });
    }

    try {
      const me = await authService.getCurrentUser();
      const meUser = (me.data as { user?: { profile?: { mustChangePassword?: boolean } } })?.user;
      if (meUser?.profile?.mustChangePassword) {
        notificationService.info(t('auth.mustChangePasswordNotice'), { duration: 2500 });
        closeAuthModal();
        navigate('/account/security', { replace: true });
        return;
      }
    } catch {
      // Token already stored by authService; continue.
    }

    notifyAuthenticated();
  };

  /** Method tabs: SegmentedControl plays the click sound itself. */
  const switchTab = (next: AuthTab) => {
    setTab(next);
    setOtp('');
    setOtpCooldown(0);
    resetUsername2FA();
    resetErrors();
  };

  const handleTab = (next: AuthTab) => {
    playClickSound();
    switchTab(next);
  };

  const sendUsername2FACode = async (target: string) => {
    if (sendingCode || otpCooldown > 0) return false;
    const id = target.trim();
    if (!id) {
      reportError(
        t(
          'auth.twoFactorEmailUnavailable',
          'Could not send a code to your registered email. Please try again or contact support.',
        ),
      );
      return false;
    }
    setSendingCode(true);
    try {
      const result = await authService.sendLoginCode(id);
      if (!result.success) {
        reportError(result.message || t('auth.failedToSendCode', 'Failed to send code'));
        if (result.cooldownSeconds) setOtpCooldown(result.cooldownSeconds);
        return false;
      }
      const resolvedEmail = (result.email || (authService.validateEmail(id).isValid ? id : twoFactorEmail) || '').trim();
      const masked =
        result.maskedEmail ||
        (resolvedEmail ? authService.maskEmail(resolvedEmail) : twoFactorMaskedEmail) ||
        '';
      if (resolvedEmail) setTwoFactorEmail(resolvedEmail);
      if (masked) setTwoFactorMaskedEmail(masked);
      setOtpCooldown(result.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
      notificationService.success(
        masked
          ? t('auth.twoFactorCodeSent', 'Verification code sent to {{email}}', { email: masked })
          : t('auth.verificationSentEmail', 'Verification code sent to your email'),
      );
      return true;
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        t('auth.failedToSendVerification', 'Failed to send verification code. Please try again.');
      reportError(msg);
      return false;
    } finally {
      setSendingCode(false);
    }
  };

  const enterUsername2FA = async (opts: {
    email?: string;
    maskedEmail?: string;
    codeSent?: boolean;
    cooldownSeconds?: number;
  }) => {
    const emailForCode = (opts.email || '').trim();
    const masked = opts.maskedEmail || (emailForCode ? authService.maskEmail(emailForCode) : '');
    setUsername2FA(true);
    setTwoFactorEmail(emailForCode);
    setTwoFactorMaskedEmail(masked);
    setOtp('');
    setOtpCooldown(0);
    resetErrors();

    // Backend already emailed OTP on the credential challenge.
    if (opts.codeSent) {
      setOtpCooldown(opts.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
      notificationService.success(
        masked
          ? t('auth.twoFactorCodeSent', 'Verification code sent to {{email}}', { email: masked })
          : t('auth.verificationSentEmail', 'Verification code sent to your email'),
      );
      return;
    }

    // Prefer registered email from API; otherwise resend via login username/identifier.
    const sendTarget = emailForCode || username.trim();
    await sendUsername2FACode(sendTarget);
  };

  const handleSendCode = async () => {
    if (sendingCode || otpCooldown > 0 || busy) return;
    resetErrors();
    playClickSound();

    if (username2FA) {
      await sendUsername2FACode(twoFactorEmail.trim() || username.trim());
      return;
    }

    setSendingCode(true);
    try {
      if (tab === 'email') {
        const emailVal = email.trim();
        if (!emailVal) {
          setError(t('auth.emailRequired', 'Email address is required'));
          return;
        }
        const emailCheck = authService.validateEmail(emailVal);
        if (!emailCheck.isValid) {
          setError(emailCheck.message);
          return;
        }
        const result = await authService.sendEmailOTP(emailVal);
        if (!result.success) {
          reportError(result.message || t('auth.failedToSendCode', 'Failed to send code'));
          if (result.cooldownSeconds) setOtpCooldown(result.cooldownSeconds);
          return;
        }
        setOtpCooldown(result.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
        notificationService.success(t('auth.verificationSentEmail', 'Verification code sent to your email'));
        return;
      }

      const phoneVal = phone.trim();
      if (!phoneVal) {
        setError(t('auth.phoneRequired', 'Phone number is required'));
        return;
      }
      const phoneCheck = authService.validatePhone(phoneVal);
      if (!phoneCheck.isValid) {
        setError(phoneCheck.message);
        return;
      }
      const result = await authService.sendPhoneLoginCode(phoneVal);
      if (!result.success) {
        reportError(result.message || t('auth.failedToSendCode', 'Failed to send code'));
        if (result.cooldownSeconds) setOtpCooldown(result.cooldownSeconds);
        return;
      }
      setOtpCooldown(result.cooldownSeconds ?? DEFAULT_RESEND_COOLDOWN_SECONDS);
      notificationService.success(t('auth.verificationSentPhone', 'Verification code sent to your phone'));
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        t('auth.failedToSendVerification', 'Failed to send verification code. Please try again.');
      reportError(msg);
    } finally {
      setSendingCode(false);
    }
  };

  const toggleMode = () => {
    playClickSound();
    resetErrors();
    setOtp('');
    setOtpCooldown(0);
    resetUsername2FA();
    if (!isRegister && tab === 'username') setTab('phone');
    setAuthMode(isRegister ? 'signin' : 'register');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    resetErrors();
    playClickSound();
    setBusy(true);
    try {
      if (!isRegister && tab === 'username') {
        if (username2FA) {
          const code = otp.trim();
          if (!code) {
            reportError(t('auth.verificationCodeRequired', 'Verification code is required'));
            return;
          }
          if (code.length !== 6) {
            reportError(t('auth.verificationCodeMustBe6', 'Verification code must be 6 digits'));
            return;
          }
          const result = await authService.authenticateUser(
            {
              username: username.trim(),
              email: twoFactorEmail || '',
              password,
              emailCode: code,
              phone: '',
              phoneCode: '',
            },
            true,
          );
          if (!result.success) {
            reportError(result.message || t('auth.loginFailed', 'Sign in failed'));
            return;
          }
          await completeLogin();
          return;
        }

        if (!username.trim() || !password) {
          setError(t('auth.usernamePasswordRequired', 'Username and password are required'));
          return;
        }

        const probe = await authService.validateCredentialsWithAPI(username.trim(), password);
        if (!probe.success) {
          reportError(probe.message || t('auth.loginFailed', 'Sign in failed'));
          if (probe.cooldownSeconds) setOtpCooldown(probe.cooldownSeconds);
          return;
        }

        if (!probe.two_factor_enabled) {
          // Token already stored by validate (email) or login probe (username).
          await completeLogin();
          return;
        }

        await enterUsername2FA({
          email: probe.email,
          maskedEmail: probe.maskedEmail,
          codeSent: probe.codeSent,
          cooldownSeconds: probe.cooldownSeconds,
        });
        return;
      }

      if (tab === 'email') {
        if (!email.trim()) {
          setError(t('auth.emailRequired', 'Email address is required'));
          return;
        }
        if (!otp.trim()) {
          setError(t('auth.verificationCodeRequired', 'Verification code is required'));
          return;
        }
        if (otp.trim().length !== 6) {
          setError(t('auth.verificationCodeMustBe6', 'Verification code must be 6 digits'));
          return;
        }
        const result = await authService.loginWithEmailCode(email.trim(), otp.trim());
        if (!result.success) {
          reportError(result.message || t('auth.loginFailed', 'Sign in failed'));
          return;
        }
        await completeLogin({ isRegister });
        return;
      }

      if (!phone.trim()) {
        setError(t('auth.phoneRequired', 'Phone number is required'));
        return;
      }
      if (!otp.trim()) {
        setError(t('auth.verificationCodeRequired', 'Verification code is required'));
        return;
      }
      if (otp.trim().length !== 6) {
        setError(t('auth.verificationCodeMustBe6', 'Verification code must be 6 digits'));
        return;
      }
      const result = await authService.loginWithPhoneCode(phone.trim(), otp.trim());
      if (!result.success) {
        reportError(result.message || t('auth.loginFailed', 'Sign in failed'));
        return;
      }
      await completeLogin({ isRegister });
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || t('auth.loginFailedTryAgain', 'Something went wrong');
      reportError(msg);
    } finally {
      setBusy(false);
    }
  };

  const visibleTabs: readonly [AuthTab, string, typeof UserIcon][] = isRegister
    ? [
        ['phone', 'Phone', Phone],
        ['email', 'Email', Mail],
      ]
    : [
        ['username', 'Username', UserIcon],
        ['phone', 'Phone', Phone],
        ['email', 'Email', Mail],
      ];

  const tabItems: SegmentItem<AuthTab>[] = visibleTabs.map(([id, label, Icon]) => ({
    value: id,
    label: <AuthMethodLabel icon={<Icon aria-hidden />} text={t(`auth.tab.${id}`, label)} />,
  }));

  const field1Label =
    tab === 'username'
      ? t('auth.username', 'Username')
      : tab === 'phone'
        ? t('auth.phone', 'Phone number')
        : t('auth.email', 'Email address');

  const contactReady = tab === 'email' ? !!email.trim() : tab === 'phone' ? !!phone.trim() : false;
  const canSendCode =
    ((otpTab && !username2FA && contactReady) ||
      (username2FA && !!(twoFactorEmail.trim() || username.trim()))) &&
    !sendingCode &&
    otpCooldown <= 0 &&
    !busy;
  const canSubmit = otpTab ? otp.trim().length === 6 : !!username.trim() && !!password;

  const ctaLabel = isRegister
    ? t('auth.createAccount', 'Create account')
    : username2FA
      ? t('auth.verifyAndSignIn', 'Verify & sign in')
      : t('header.signIn', 'Sign in');

  const title = isRegister
    ? t('header.register', 'Register')
    : username2FA
      ? t('auth.twoFactorTitle', 'Two-factor authentication')
      : t('header.signIn', 'Sign in');

  const sectionLabel =
    username2FA
      ? null
      : tab === 'phone'
        ? t('auth.phoneLogin', 'Phone Login')
        : tab === 'email'
          ? t('auth.emailLogin', 'Email Login')
          : null;

  /** In the 2FA step the hint ("Enter the 6-digit code sent to …") labels the code input. */
  const otpLabel = username2FA
    ? twoFactorMaskedEmail
      ? t('auth.twoFactorHint', 'Enter the 6-digit code sent to {{email}}', { email: twoFactorMaskedEmail })
      : t('auth.twoFactorHintGeneric', 'Enter the 6-digit code sent to your registered email')
    : t('auth.verificationCode', 'Verification Code');

  return (
    <Dialog
      open={isOpen}
      onClose={closeAuthModal}
      title={<span id="auth-mdl-title">{title}</span>}
      size="md"
      closeLabel={t('common.close')}
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {orderSaved && (
          <Badge tone="success" dot={false} className="self-start">
            {t('auth.orderSaved', 'Your order is saved ✓')}
          </Badge>
        )}

        {!username2FA && (
          <SegmentedControl<AuthTab>
            aria-label={t('auth.methodLabel')}
            items={tabItems}
            value={tab}
            onChange={switchTab}
          />
        )}

        {isRegister && !username2FA && (
          <p className="m-0 font-ui text-small text-secondary">
            {t(
              'auth.registerPhoneOrEmailOnly',
              'New accounts can only be created with phone or email — username sign-in is for existing accounts.',
            )}
          </p>
        )}

        {!username2FA && (
          <div className="flex w-full flex-col gap-3">
            {sectionLabel && (
              <AuthSectionLabel icon={tab === 'phone' ? <Phone aria-hidden /> : <Mail aria-hidden />} text={sectionLabel} />
            )}

            {tab === 'phone' ? (
              <Field label={field1Label} hint={t('auth.phoneCountryHint', 'Select country and enter your phone number')}>
                <AuthPhoneInput
                  countryOptionsOrder={['US', 'CA', 'GB', 'VN', '…']}
                  placeholder={t('auth.phoneIntlPlaceholder', 'Phone number')}
                  value={phone}
                  onChange={setPhone}
                  numberInputProps={{ 'aria-label': field1Label }}
                />
              </Field>
            ) : (
              <Field label={field1Label}>
                <Input
                  leading={tab === 'email' ? <Mail /> : <UserIcon />}
                  placeholder={field1Label}
                  type={tab === 'email' ? 'email' : 'text'}
                  autoComplete={tab === 'username' ? 'username' : 'email'}
                  value={tab === 'username' ? username : email}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (tab === 'username') setUsername(v);
                    else setEmail(v);
                  }}
                />
              </Field>
            )}
          </div>
        )}

        {otpTab ? (
          <Field
            label={otpLabel}
            action={
              // handleSendCode plays the click sound itself.
              <Button
                type="button"
                variant="secondary"
                silent
                className="min-w-24"
                disabled={!canSendCode}
                loading={sendingCode}
                onClick={handleSendCode}
              >
                {sendingCode
                  ? null
                  : otpCooldown > 0
                    ? `${otpCooldown}s`
                    : username2FA
                      ? t('auth.resendCode', 'Resend')
                      : t('auth.getCode', 'GET CODE')}
              </Button>
            }
          >
            <Input
              placeholder="000000"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </Field>
        ) : (
          <Field label={t('auth.password', 'Password')}>
            <PasswordInput
              placeholder={t('auth.password', 'Password')}
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}

        {error && (
          <Alert tone="danger">
            <span className="whitespace-pre-wrap">{error}</span>
          </Alert>
        )}

        {/* handleSubmit plays the click sound (also on Enter). */}
        <Button type="submit" variant="primary" fullWidth silent disabled={busy || !canSubmit} loading={busy}>
          {busy ? t('common.loading', '…') : ctaLabel}
        </Button>

        <div
          className={cx(
            'flex items-center gap-2',
            username2FA || (!isRegister && tab === 'username') || isRegister ? 'justify-between' : 'justify-end',
          )}
        >
          {username2FA ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              silent
              onClick={() => {
                playClickSound();
                resetUsername2FA();
                setOtp('');
                setOtpCooldown(0);
                resetErrors();
              }}
            >
              {t('auth.backToPassword', 'Back')}
            </Button>
          ) : (
            !isRegister &&
            tab === 'username' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                silent
                onClick={() => {
                  playClickSound();
                  closeAuthModal();
                  navigate(FORGOT_PASSWORD_LOGIN_PATH);
                }}
              >
                {t('auth.forgotPassword', 'Forgot password?')}
              </Button>
            )
          )}
          {!username2FA && isRegister && (
            <Button type="button" variant="ghost" size="sm" silent onClick={toggleMode}>
              {t('auth.haveAccount', 'Have an account? Sign in')}
            </Button>
          )}
        </div>

        {!isRegister && !username2FA && tab === 'username' && (
          <p className="m-0 text-center font-ui text-small text-secondary">
            {t('auth.noAccountHintPrefix', 'No account? You can also login via')}{' '}
            <button type="button" onClick={() => handleTab('phone')} className={inlineLinkClass}>
              {t('auth.tab.phone', 'phone number')}
            </button>{' '}
            {t('common.or', 'or')}{' '}
            <button type="button" onClick={() => handleTab('email')} className={inlineLinkClass}>
              {t('auth.tab.email', 'email')}
            </button>
            .
          </p>
        )}
      </form>
    </Dialog>
  );
}
